import React, { useState, useEffect } from 'react';
import axios from 'axios';

const LoginPage = ({ onLogin, onForgotPassword }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Handle Google OAuth callback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const errorParam = urlParams.get('error');

    if (token) {
      // Auto-login with Google token
      onLogin({ token });
      // Clean up URL
      window.history.replaceState({}, document.title, '/login');
    } else if (errorParam) {
      const errorMessages = {
        'oauth_failed': 'Google sign-in failed. Please try again.',
        'invalid_domain': 'Only @caamuseum.org email addresses are allowed.',
        'invalid_client': 'Invalid client configuration.',
        'account_disabled': 'Your account has been disabled.'
      };
      setError(errorMessages[errorParam] || 'An error occurred during sign-in.');
      // Clean up URL
      window.history.replaceState({}, document.title, '/login');
    }
  }, [onLogin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/login`,
        {
          username,
          password,
          clientCode: 'caam'
        }
      );
      onLogin(response.data);
    } catch (err) {
      setError('Invalid credentials. Please try again.');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setError('');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/auth/google/url?clientCode=caam`
      );
      // Redirect to Google OAuth
      window.location.href = response.data.url;
    } catch (err) {
      setError('Failed to initiate Google sign-in. Please try again.');
      console.error('Google OAuth error:', err);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#ffffff'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '400px',
        padding: '40px',
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        border: '1px solid var(--gray-200)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ background: "#000", borderRadius: "12px", padding: "16px 24px", marginBottom: "16px", display: "inline-block" }}>
            <img src="/caam-logo.png" alt="CAAM" style={{ height: "50px" }} />
          </div>
          <p style={{ color: 'var(--gray-500)', fontSize: '14px' }}>Intranet Portal</p>
        </div>

        {error && (
          <div className="alert alert-danger" style={{ marginBottom: '20px' }}>
            {error}
          </div>
        )}

        {/* Google Sign-In Button */}
        <button
          type="button"
          onClick={handleGoogleSignIn}
          style={{
            width: '100%',
            padding: '12px',
            marginBottom: '24px',
            background: 'white',
            border: '1px solid var(--gray-300)',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            color: 'var(--gray-700)',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => e.currentTarget.style.background = 'var(--gray-50)'}
          onMouseOut={(e) => e.currentTarget.style.background = 'white'}
        >
          <svg width="18" height="18" viewBox="0 0 18 18">
            <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
            <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
            <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.55 0 9s.348 2.825.957 4.039l3.007-2.332z"/>
            <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.163 6.656 3.58 9 3.58z"/>
          </svg>
          Sign in with Google
        </button>

        {/* Divider */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '24px',
          color: 'var(--gray-400)',
          fontSize: '12px'
        }}>
          <div style={{ flex: 1, height: '1px', background: 'var(--gray-300)' }}></div>
          <span style={{ padding: '0 12px' }}>OR</span>
          <div style={{ flex: 1, height: '1px', background: 'var(--gray-300)' }}></div>
        </div>

        {/* Traditional Login Form */}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Username</label>
            <input
              type="text"
              className="form-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <input
              type="password"
              className="form-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          {/* Self-contained styling: the shared .btn-primary relies on --gradient-accent,
              which isn't in scope on the login screen (collapses to a transparent, invisible
              button). Inline styles here guarantee a visible submit button. */}
          <button
            type="submit"
            disabled={loading}
            onMouseEnter={e => { if (!e.currentTarget.disabled) e.currentTarget.style.background = '#000'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#171717'; }}
            style={{
              width: '100%', marginTop: '8px', padding: '12px',
              background: '#171717', color: '#fff', border: 'none', borderRadius: '8px',
              fontSize: '14px', fontWeight: 600,
              cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>

          <div style={{ textAlign:'center', marginTop:'16px' }}>
            <button 
              type="button" 
              onClick={() => onForgotPassword && onForgotPassword()} 
              style={{ 
                background:'none', 
                border:'none', 
                color:'#6B7280', 
                fontSize:'12px', 
                cursor:'pointer' 
              }}
            >
              Forgot your password?
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
